/**
 * Source-side redaction. Every string the SDK is about to emit passes through here FIRST,
 * inside the browser, so a sensitive value can never reach the wire even when it appears
 * inside an otherwise harmless label (a masked account number in a heading, a reference
 * containing a PAN).
 *
 * Length is preserved so the assistant can still reason about the shape of a field, and
 * SHORT numbers are deliberately left intact: amounts are what guidance is usually about.
 */
import type { PrivacyRules, RedactPattern } from "./types.js";

const PATTERNS: Record<RedactPattern, RegExp> = {
  // Indian permanent account number: 5 letters, 4 digits, 1 letter.
  PAN: /\b[A-Z]{5}[0-9]{4}[A-Z]\b/g,
  // 12 digits, optionally spaced in groups of four.
  AADHAAR: /\b\d{4}\s?\d{4}\s?\d{4}\b/g,
  // Bank branch identifier: 4 letters, a zero, then 6 alphanumerics.
  IFSC: /\b[A-Z]{4}0[A-Z0-9]{6}\b/g,
  // 4 to 8 digit runs cover OTPs and PINs.
  OTP: /\b\d{4,8}\b/g,
  // Long digit runs, spaced or not, cover card and account numbers.
  CARD: /\b(?:\d[ -]?){12,19}\b/g,
};

const ALL: RedactPattern[] = ["PAN", "AADHAAR", "IFSC", "OTP", "CARD"];

/**
 * Application order, regardless of the order the caller listed patterns in. CARD must run
 * before AADHAAR: a spaced 16-digit card number ("4539 1488 0343 6467") contains a 12-digit
 * prefix that the Aadhaar pattern would otherwise eat first, leaving the last group exposed.
 * OTP runs last, on whatever digits the identity patterns left behind.
 */
const APPLY_ORDER: RedactPattern[] = ["PAN", "CARD", "AADHAAR", "IFSC", "OTP"];

/**
 * A 4-8 digit run is OTP/PIN shaped, but it is also amount/balance/EMI shaped — and guidance
 * is usually ABOUT amounts. So the OTP pattern applies only when the string itself says it is
 * a code: "OTP sent: 123456" is destroyed, "Amount: 2500" is not. The one ambiguity this
 * cannot resolve is the Indian postal "PIN code", which is redacted too — a masked pincode in
 * a label is a far smaller loss than every amount on the page turning into asterisks.
 */
const OTP_CONTEXT = /otp|one[- ]?time|code|pin/i;

/** Replace each character of a match with an asterisk, preserving length and separators. */
function mask(m: string): string {
  return m.replace(/[^\s-]/g, "*");
}

export class Redactor {
  private patterns: Array<{ name: RedactPattern; re: RegExp }> = [];
  private custom: RegExp[] = [];

  constructor(rules?: PrivacyRules) {
    this.update(rules);
  }

  update(rules?: PrivacyRules): void {
    const names = new Set(rules?.redact ?? ALL);
    this.patterns = APPLY_ORDER.filter((n) => names.has(n)).map((n) => ({ name: n, re: PATTERNS[n] }));
    this.custom = (rules?.redactCustom ?? []).filter((r) => r instanceof RegExp);
  }

  /** Redact a single string. Safe on undefined. Never throws. */
  text(s: string | undefined | null): string {
    if (!s) return "";
    let out = String(s);
    try {
      // CARD before OTP: a 16 digit run should be masked as a card, not chopped into OTPs.
      for (const { name, re } of this.patterns) {
        if (name === "OTP" && !OTP_CONTEXT.test(out)) continue;
        out = out.replace(new RegExp(re.source, re.flags), mask);
      }
      for (const re of this.custom) out = out.replace(new RegExp(re.source, re.flags.includes("g") ? re.flags : re.flags + "g"), mask);
    } catch {
      // A pathological pattern must never break the session. Emit nothing rather than raw text.
      return "";
    }
    return out;
  }
}

/** Input types that can never be labelled or filled, regardless of configuration. */
export const ALWAYS_SENSITIVE_TYPES = new Set(["password"]);

/**
 * Name/id/autocomplete hints that mark a field as sensitive even when its type is text.
 * The cc-* and one-time-code tokens are the STANDARD autocomplete values — a field marked
 * only with autocomplete="cc-number" carries no other sensitive hint at all.
 */
const SENSITIVE_HINT = /(otp|pin\b|cvv|cvc|card|passcode|password|secret|security[- ]?code|aadhaar|upi[- ]?pin|cc-(?:number|csc|exp)|one[- ]?time[- ]?code)/i;

/** Is this element one whose value must never be observable or fillable? */
export function isSensitiveField(el: Element, maskInputTypes: Set<string>): boolean {
  const tag = el.tagName.toLowerCase();
  if (tag !== "input" && tag !== "textarea") return false;
  const input = el as HTMLInputElement;
  const type = (input.type || "text").toLowerCase();
  if (ALWAYS_SENSITIVE_TYPES.has(type) || maskInputTypes.has(type)) return true;
  const hint = [input.name, input.id, input.getAttribute("autocomplete"), input.getAttribute("aria-label")]
    .filter(Boolean).join(" ");
  if (hint && SENSITIVE_HINT.test(hint)) return true;
  // inputmode=numeric with a short maxlength is the common OTP box.
  const maxLen = Number(input.getAttribute("maxlength") || 0);
  if (maxLen > 0 && maxLen <= 8 && (input.inputMode === "numeric" || type === "tel" || type === "number")) return true;
  return false;
}
